import {
    Popover,
    PopoverInteractionKind,
    PopoverPosition,
    Spinner
} from '@blueprintjs/core';
import { IConfigurationValuesService } from '@services/ConfigurationValuesService';
import { createConfigurationValuesService } from '@services/factories';
import { map } from 'lodash';
import * as React from 'react';
import { ClientTrackingCategories } from '@models/ClientTrackingCategoryType';
import { ClientTrackingNames } from '@models/ClientTrackingNameType';
import TrackingService, { ITrackingService } from '../../services/TrackingService';
import TranslationService, { ITranslationService } from '../../services/TranslationService';
import { Button } from '@styles/shared-styles';
import { themeProvider } from '@swipejobs/frontend-theme-provider';
import '../../styles/css/index.css';
import styled from 'styled-components';

const LanguageSelectorButton = styled(Button)`
  background-color: ${() => themeProvider.getColors().general.tertiaryColor};

  @media (max-width: 800px) {
    min-width: 100px;
    min-height: 24px;
    font-size: ${() => themeProvider.getTypography().font.size.normal};
  }

  @media (max-width: 550px) {
    min-width: 60px;
    min-height: 20px;
    font-size: ${() => themeProvider.getTypography().font.size.small};
  }
`;

const ThemedInteractableText = styled.div`
  text-transform: ${() => themeProvider.getTypography().button.text.textTransform};
`;

export interface State {
    isOpen: boolean;
    language: string;
    languages: string[];
    loaders: Array<{
        [lang: string]: boolean;
    }>;
}

class LanguageSelector extends React.Component<{}, State> {
    private readonly translationService: ITranslationService;
    private readonly trackingService: ITrackingService;
    private readonly configurationValuesService: IConfigurationValuesService;

    constructor(props: any) {
        super(props);
        this.translationService = TranslationService.getInstance();
        this.trackingService = new TrackingService();
        this.configurationValuesService = createConfigurationValuesService();

        const language = this.translationService.getLanguage();
        const languages = this.translationService.getLanguages();
        const loaders = map(languages, item => ({ [item]: false }));

        this.state = {
            isOpen: false,
            language,
            languages,
            loaders
        };
    }

    private async changeLanguageAsync(language: string) {
        const loaders = this.state.loaders;
        loaders[language] = true;
        await this.setState({ loaders });
        await this.translationService.changeLanguageAsync(language);
        loaders[language] = false;

        this.trackingService.trackEvent(
            ClientTrackingCategories.Language,
            ClientTrackingNames.ChangeLanguage,
            language
        );

        this.setState({
            isOpen: false,
            language,
            loaders
        });
    }

    private getContent() {
        const languages = this.translationService.translateObject('languageNames');

        return (
            <div className='select-lang-wrapper'>
                <ThemedInteractableText className='select-lang-title'>
                    {this.translationService.translate('selectLanguage')}
                </ThemedInteractableText>
                {
                    this.state.languages
                        .filter(item => {
                            // replace with a more scalable language configuration later
                            if (item.toLowerCase() !== 'es') return true;

                            return this.configurationValuesService.enableLanguageES;
                        })
                        .map(item => (
                            <div className='select-lang-item'
                                 onClick={() => this.changeLanguageAsync(item)}
                                 key={item}>
                                <ThemedInteractableText className='select-lang-item-content'>
                                    {languages[item]}
                                </ThemedInteractableText>
                                <div className='select-lang-item-loader'>
                                    {
                                        this.state.loaders[item] && <Spinner size={Spinner.SIZE_SMALL}/>
                                    }
                                </div>
                            </div>
                        ))
                }
            </div>
        );
    }

    private handleInteraction(nextOpenState: boolean) {
        this.setState({ isOpen: nextOpenState });
    }

    public render() {
        const languages = this.translationService.translateObject('languageNames');

        return (
            <div className='language-selector'>
                <Popover
                    className='popover'
                    position={PopoverPosition.BOTTOM_RIGHT}
                    interactionKind={PopoverInteractionKind.CLICK}
                    captureDismiss={true}
                    content={this.getContent()}
                    enforceFocus={false}
                    onInteraction={(state) => this.handleInteraction(state)}
                    isOpen={this.state.isOpen}
                >
                    <LanguageSelectorButton>{languages[this.state.language]}</LanguageSelectorButton>
                </Popover>
            </div>
        );
    }
}

export default LanguageSelector;



// WEBPACK FOOTER //
// ./src/components/LanguageSelector/index.tsx